import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Building2, 
  MapPin, 
  DollarSign, 
  Calendar, 
  FileText, 
  AlertCircle,
  Save,
  X
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const JobForm = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [locations, setLocations] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm();

  const watchSalaryType = watch('salary_type', 'yearly');

  useEffect(() => {
    fetchReferenceData();
    if (isEdit && id) {
      fetchJob();
    }
  }, [isEdit, id]);

  const fetchReferenceData = async () => {
    try {
      const [categoriesRes, typesRes, locationsRes] = await Promise.all([
        api.get('/api/categories/'),
        api.get('/api/types/'),
        api.get('/api/locations/')
      ]);
      
      console.log('Categories response:', categoriesRes.data);
      console.log('Types response:', typesRes.data);
      console.log('Locations response:', locationsRes.data);
      
      // Handle different response structures
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : categoriesRes.data?.results || []);
      setJobTypes(Array.isArray(typesRes.data) ? typesRes.data : typesRes.data?.results || []);
      setLocations(Array.isArray(locationsRes.data) ? locationsRes.data : locationsRes.data?.results || []);
    } catch (error) {
      toast.error('Failed to load form data');
      console.error('Error fetching reference data:', error);
      // Set empty arrays as fallback
      setCategories([]);
      setJobTypes([]);
      setLocations([]);
    } finally {
      setDataLoading(false);
    }
  };

  const fetchJob = async () => {
    try {
      const response = await api.get(`/api/jobs/${id}/`);
      const job = response.data;
      
      // Set form values
      Object.keys(job).forEach(key => {
        if (job[key] !== null && job[key] !== undefined) {
          setValue(key, job[key]);
        }
      });
    } catch (error) {
      toast.error('Failed to load job details');
      console.error('Error fetching job:', error);
      navigate('/dashboard');
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (isEdit) {
        await api.patch(`/api/jobs/${id}/`, data);
        toast.success('Job updated successfully');
      } else {
        await api.post('/api/jobs/', data);
        toast.success('Job created successfully');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(isEdit ? 'Failed to update job' : 'Failed to create job');
      console.error('Error saving job:', error);
    } finally {
      setLoading(false);
    }
  };

  if (dataLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading form data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900">
                {isEdit ? 'Edit Job Posting' : 'Create New Job Posting'}
              </h1>
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-8">
            {/* Basic Information */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Title *
                  </label>
                  <input
                    {...register('title', { required: 'Job title is required' })}
                    type="text"
                    className={`input-field ${errors.title ? 'border-red-300 focus:ring-red-500' : ''}`}
                    placeholder="e.g., Senior Software Engineer"
                  />
                  {errors.title && (
                    <div className="mt-1 flex items-center text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.title.message}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    {...register('category', { required: 'Category is required' })}
                    className={`input-field ${errors.category ? 'border-red-300 focus:ring-red-500' : ''}`}
                  >
                    <option value="">Select a category</option>
                    {Array.isArray(categories) && categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <div className="mt-1 flex items-center text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.category.message}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Type *
                  </label>
                  <select
                    {...register('job_type', { required: 'Job type is required' })}
                    className={`input-field ${errors.job_type ? 'border-red-300 focus:ring-red-500' : ''}`}
                  >
                    <option value="">Select job type</option>
                    {Array.isArray(jobTypes) && jobTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  {errors.job_type && (
                    <div className="mt-1 flex items-center text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.job_type.message}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location *
                  </label>
                  <select
                    {...register('location', { required: 'Location is required' })}
                    className={`input-field ${errors.location ? 'border-red-300 focus:ring-red-500' : ''}`}
                  >
                    <option value="">Select location</option>
                    {Array.isArray(locations) && locations.map(location => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                  {errors.location && (
                    <div className="mt-1 flex items-center text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.location.message}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experience Level *
                  </label>
                  <select
                    {...register('experience_level', { required: 'Experience level is required' })}
                    className={`input-field ${errors.experience_level ? 'border-red-300 focus:ring-red-500' : ''}`}
                  >
                    <option value="">Select experience level</option>
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior Level</option>
                    <option value="executive">Executive</option>
                  </select>
                  {errors.experience_level && (
                    <div className="mt-1 flex items-center text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.experience_level.message}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Job Description */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Job Description</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Description *
                  </label>
                  <textarea
                    {...register('description', { required: 'Job description is required' })}
                    rows={6}
                    className={`input-field ${errors.description ? 'border-red-300 focus:ring-red-500' : ''}`}
                    placeholder="Describe the role, responsibilities, and what makes this opportunity unique..."
                  />
                  {errors.description && (
                    <div className="mt-1 flex items-center text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.description.message}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Requirements *
                  </label>
                  <textarea
                    {...register('requirements', { required: 'Requirements are required' })}
                    rows={4}
                    className={`input-field ${errors.requirements ? 'border-red-300 focus:ring-red-500' : ''}`}
                    placeholder="List the required skills, qualifications, and experience..."
                  />
                  {errors.requirements && (
                    <div className="mt-1 flex items-center text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.requirements.message}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Responsibilities
                  </label>
                  <textarea
                    {...register('responsibilities')}
                    rows={4}
                    className="input-field"
                    placeholder="Outline the key responsibilities and duties..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Benefits & Perks
                  </label>
                  <textarea
                    {...register('benefits')}
                    rows={3}
                    className="input-field"
                    placeholder="List benefits, perks, and what you offer employees..."
                  />
                </div>
              </div>
            </div>

            {/* Salary Information */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Salary Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Salary
                  </label>
                  <input
                    {...register('salary_min', { 
                      valueAsNumber: true,
                      min: { value: 0, message: 'Salary must be positive' }
                    })}
                    type="number"
                    step="0.01"
                    className={`input-field ${errors.salary_min ? 'border-red-300 focus:ring-red-500' : ''}`}
                    placeholder="50000"
                  />
                  {errors.salary_min && (
                    <div className="mt-1 flex items-center text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.salary_min.message}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Salary
                  </label>
                  <input
                    {...register('salary_max', { 
                      valueAsNumber: true,
                      min: { value: 0, message: 'Salary must be positive' }
                    })}
                    type="number"
                    step="0.01"
                    className={`input-field ${errors.salary_max ? 'border-red-300 focus:ring-red-500' : ''}`}
                    placeholder="80000"
                  />
                  {errors.salary_max && (
                    <div className="mt-1 flex items-center text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.salary_max.message}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Salary Type
                  </label>
                  <select
                    {...register('salary_type')}
                    className="input-field"
                  >
                    <option value="yearly">Yearly</option>
                    <option value="monthly">Monthly</option>
                    <option value="hourly">Hourly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency
                  </label>
                  <select
                    {...register('currency')}
                    className="input-field"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                    <option value="AUD">AUD</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    {...register('is_salary_negotiable')}
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">
                    Salary is negotiable
                  </label>
                </div>
              </div>
            </div>

            {/* Application Information */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Application Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Application Deadline
                  </label>
                  <input
                    {...register('application_deadline')}
                    type="datetime-local"
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Application Email
                  </label>
                  <input
                    {...register('application_email', {
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    type="email"
                    className={`input-field ${errors.application_email ? 'border-red-300 focus:ring-red-500' : ''}`}
                    placeholder="hr@company.com"
                  />
                  {errors.application_email && (
                    <div className="mt-1 flex items-center text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.application_email.message}
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Application URL
                  </label>
                  <input
                    {...register('application_url', {
                      pattern: {
                        value: /^https?:\/\/.+/,
                        message: 'Please enter a valid URL'
                      }
                    })}
                    type="url"
                    className={`input-field ${errors.application_url ? 'border-red-300 focus:ring-red-500' : ''}`}
                    placeholder="https://company.com/apply"
                  />
                  {errors.application_url && (
                    <div className="mt-1 flex items-center text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.application_url.message}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Job Settings */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Job Settings</h2>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    {...register('is_active')}
                    type="checkbox"
                    defaultChecked
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">
                    Job is active and visible to candidates
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    {...register('is_featured')}
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">
                    Feature this job (highlighted in search results)
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    {...register('is_remote_friendly')}
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">
                    Remote work is available for this position
                  </label>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center space-x-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>{loading ? 'Saving...' : (isEdit ? 'Update Job' : 'Create Job')}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default JobForm;
